import { Descriptions, Grid, Tabs, Tag } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetRouterSpecsQuery } from '../../../api/queries/getRouterSpecs';
import LoadingElement from '../../../components/LoadingElement';
import NotFoundResult from '../../../components/results/NotFoundResult';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import { getIsPrivate } from './endpoints';
import RouterActions from './RouterActions';
import RouterDetailsFooter from './RouterDetailsFooter';
import RouterTabs from './RouterTabs';

const RouterDetailsPage = () => {
    const navigate = useNavigate();
    const { routerId } = useParams<{ routerId: string }>();
    const { defaultTab } = useLocation().state || { defaultTab: 'layout' };
    const { t } = useTranslation(['routerDetails']);

    const { data, loading } = useGetRouterSpecsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { routerId },
    });

    const screens = Grid.useBreakpoint();

    const router = data?.router;

    const defaultUpdatedValue = {
        createdAt: '',
        createdBy: null,
        updatedAt: '',
        updatedBy: null,
        __typename: 'SimpleVersioning',
    };

    const { updated, offset } =
        useFormattedSimpleVersioning({
            versioning: router ? router.versioning : defaultUpdatedValue,
            timeZone: router ? router.company.timeZone : null,
        }) || {};

    const { hasPublicEndpoints = false, hasAuthentication = false } = useMemo(() => {
        if (!router) {
            return {};
        }

        const privateStates = router.endpoints.map(getIsPrivate);

        return {
            hasPublicEndpoints: privateStates.some(state => !state),
            hasAuthentication: router.withAdmin || privateStates.some(state => state),
        };
    }, [router]);

    const [activeTab, setActiveTab] = useState(defaultTab);

    if (!router && loading) {
        return <LoadingElement />;
    }

    if (!router) {
        return <NotFoundResult />;
    }

    const content = (
        <Descriptions bordered>
            <Descriptions.Item label={t('routerDetails:contentView.hostname')}>{router.hostname}</Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.pathname')}>{router.pathname}</Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.companyName')}>
                {router.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.hasPublicEndpoints')}>
                {renderBooleanIcon(hasPublicEndpoints)}
            </Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.hasAuthentication')}>
                {renderBooleanIcon(hasAuthentication)}
            </Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.withAdmin')}>
                {renderBooleanIcon(router.withAdmin)}
            </Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.languages')}>
                {router.languages.map(language => (
                    <Tag key={language.id}>{language.referenceName}</Tag>
                ))}
            </Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.googleTagManagerId')}>
                {router.googleTagManagerId}
            </Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.linkedInInsightTagId')}>
                {router.linkedInInsightTagId}
            </Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.pixelId')}>{router.pixelId}</Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.blueKaiId')}>{router.blueKaiId}</Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.tikTokId')}>{router.tikTokId}</Descriptions.Item>
            <Descriptions.Item label={t('routerDetails:contentView.snapChatId')}>{router.snapChatId}</Descriptions.Item>

            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<RouterActions activeTab={activeTab} router={router} />}
            footer={[<RouterDetailsFooter activeTab={activeTab} router={router} />]}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <Tabs.TabPane key="layout" tab={t('routerDetails:tabs.layout')} />
                        <Tabs.TabPane key="endpoints" tab={t('routerDetails:tabs.endpoints')} />
                        <Tabs.TabPane key="menu" tab={t('routerDetails:tabs.menu')} />
                    </Tabs>
                ),
            }}
            onBack={() => navigate('/admin/system/routers')}
            title={screens.md ? t('routerDetails:title', { name: router.hostname }) : router.hostname}
        >
            <RouterTabs activeTab={activeTab} router={router} />
        </ConsolePageWithHeader>
    );
};

export default RouterDetailsPage;
